import { matchPath } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getErrorMessage } from './api';
import { compressImage, validateImageFile } from '../utils/imageCompressor';
import {
  DEFAULT_POPUP_LINK_NOTICE_TEXT,
  PopupAssetTarget,
  PopupBanner,
  PopupBannerStatus,
  PopupBannerUpsertInput,
  PopupLinkTarget,
} from '../types';

const BUCKET_NAME = 'popup-images';
const POPUP_DISMISS_PREFIX = 'popup-dismiss';
const POPUP_SESSION_PREFIX = 'popup-session';

export const POPUP_ALLOWED_ROUTE_PATTERNS = [
  '/',
  '/restaurants',
  '/restaurants/:subAdd1/:subAdd2/:title',
  '/restaurants/:id',
  '/board',
  '/board/notice',
  '/board/free',
  '/board/free/category/:categoryCode',
  '/board/free/hot',
  '/board/restaurant-info',
  '/board/restaurant-info/region/:categoryCode',
  '/board/civil-servant',
  '/board/suggestion',
  '/board/notice/:postId',
  '/board/free/:postId',
  '/board/suggestion/:postId',
  '/board/restaurant-info/:postId',
  '/board/civil-servant/:postId',
  '/terms',
  '/privacy',
  '/support',
] as const;

export const POPUP_EXCLUDED_ROUTE_PATTERNS = [
  '/kakao-test',
  '/login',
  '/register',
  '/register/terms',
  '/register/profile',
  '/board/free/write',
  '/board/suggestion/write',
  '/board/civil-servant/write',
  '/board/restaurant-info/write',
  '/profile',
  '/auth/*',
  '/admin/*',
] as const;

export const POPUP_IMAGE_GUIDES = {
  web: {
    label: '웹용 이미지',
    recommendedSize: '1200 x 1500 px',
    minimumSize: '900 x 1125 px',
    ratio: '4:5',
    maxFileSizeLabel: '2MB 이하',
    helperText: '데스크톱용 세로형 이미지입니다. 텍스트와 핵심 비주얼은 중앙 영역에 배치해 주세요.',
  },
  mobile: {
    label: '모바일용 이미지',
    recommendedSize: '1080 x 1350 px',
    minimumSize: '810 x 1013 px',
    ratio: '4:5',
    maxFileSizeLabel: '2MB 이하',
    helperText: '모바일 화면에 맞춘 세로형 이미지입니다. 작은 글자보다는 짧고 굵은 메시지를 권장합니다.',
  },
} as const;

export type PopupBannerFormState = {
  id?: string;
  title: string;
  admin_title: string;
  link_notice_text: string;
  alt_text: string;
  link_url: string;
  link_target: PopupLinkTarget;
  priority: number;
  dismiss_days: number;
  start_at: string;
  end_at: string;
  status: PopupBannerStatus;
  is_active: boolean;
  web_image_url?: string | null;
  web_storage_path?: string | null;
  web_width?: number | null;
  web_height?: number | null;
  mobile_image_url?: string | null;
  mobile_storage_path?: string | null;
  mobile_width?: number | null;
  mobile_height?: number | null;
};

export type PopupAssetUploadResult = {
  imageUrl: string;
  storagePath: string;
  width: number;
  height: number;
  fileSize: number;
};

const isBrowser = () => typeof window !== 'undefined';

const getPopupApiBaseUrl = (): string => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim();
  if (!supabaseUrl) {
    throw new Error('REACT_APP_SUPABASE_URL 설정이 필요합니다.');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1`;
};

const getPopupDismissKey = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  return `${POPUP_DISMISS_PREFIX}:${popup.id}:${popup.updated_at}`;
};

const getPopupSessionKey = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  return `${POPUP_SESSION_PREFIX}:${popup.id}:${popup.updated_at}`;
};

const isAllowedPopupImageType = (file: File) => {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
};

const makeSafeExt = (fileName: string, mimeType?: string) => {
  const byName = (fileName.split('.').pop() || '').toLowerCase();
  const byType =
    mimeType === 'image/png'
      ? 'png'
      : mimeType === 'image/webp'
        ? 'webp'
        : mimeType === 'image/jpeg'
          ? 'jpg'
          : '';

  const ext = byType || byName || 'jpg';
  return ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
};

const loadImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const result = { width: img.width, height: img.height };
      URL.revokeObjectURL(url);
      resolve(result);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지 크기를 확인할 수 없습니다.'));
    };
    img.src = url;
  });
};

const getAdminSessionUser = async () => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.user) {
    throw new Error('관리자 로그인이 필요합니다.');
  }

  const user = sessionData.session.user;
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle();

  if (profileError || profile?.role !== 'admin') {
    throw new Error('관리자 권한이 필요합니다.');
  }

  return user;
};

const normalizePopupBanner = (popup: PopupBanner): PopupBanner => ({
  ...popup,
  placement: 'service-entry',
  link_notice_text: popup.link_notice_text ?? DEFAULT_POPUP_LINK_NOTICE_TEXT,
  link_target: (popup.link_target || '_blank') as PopupLinkTarget,
  dismiss_days: popup.dismiss_days ?? 7,
});

export const createEmptyPopupFormState = (): PopupBannerFormState => ({
  title: '',
  admin_title: '',
  link_notice_text: DEFAULT_POPUP_LINK_NOTICE_TEXT,
  alt_text: '',
  link_url: '',
  link_target: '_blank',
  priority: 1,
  dismiss_days: 7,
  start_at: '',
  end_at: '',
  status: 'draft',
  is_active: false,
  web_image_url: null,
  web_storage_path: null,
  web_width: null,
  web_height: null,
  mobile_image_url: null,
  mobile_storage_path: null,
  mobile_width: null,
  mobile_height: null,
});

export const toPopupBannerFormState = (popup: PopupBanner): PopupBannerFormState => ({
  id: popup.id,
  title: popup.title,
  admin_title: popup.admin_title || '',
  link_notice_text: popup.link_notice_text ?? DEFAULT_POPUP_LINK_NOTICE_TEXT,
  alt_text: popup.alt_text || '',
  link_url: popup.link_url || '',
  link_target: popup.link_target || '_blank',
  priority: popup.priority || 1,
  dismiss_days: popup.dismiss_days || 7,
  start_at: toDatetimeLocalValue(popup.start_at),
  end_at: toDatetimeLocalValue(popup.end_at),
  status: popup.status,
  is_active: popup.is_active,
  web_image_url: popup.web_image_url || null,
  web_storage_path: popup.web_storage_path || null,
  web_width: popup.web_width || null,
  web_height: popup.web_height || null,
  mobile_image_url: popup.mobile_image_url || null,
  mobile_storage_path: popup.mobile_storage_path || null,
  mobile_width: popup.mobile_width || null,
  mobile_height: popup.mobile_height || null,
});

export const toDatetimeLocalValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

export const toIsoDatetimeOrNull = (value?: string | null) => {
  if (!value) return null;
  return new Date(value).toISOString();
};

export const getPopupBannerDisplayStatus = (popup: PopupBanner) => {
  if (popup.status === 'archived') return 'archived';
  if (!popup.is_active || popup.status === 'inactive') return 'inactive';

  const now = Date.now();
  const startAt = popup.start_at ? new Date(popup.start_at).getTime() : null;
  const endAt = popup.end_at ? new Date(popup.end_at).getTime() : null;

  if (startAt && startAt > now) {
    return 'scheduled';
  }

  if (endAt && endAt < now) {
    return 'ended';
  }

  return 'active';
};

export const formatPopupBannerDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ko-KR');
};

export const isPopupRouteAllowed = (pathname: string) => {
  const isExcluded = POPUP_EXCLUDED_ROUTE_PATTERNS.some((pattern) =>
    matchPath({ path: pattern, end: pattern !== '/auth/*' && pattern !== '/admin/*' }, pathname),
  );

  if (isExcluded) {
    return false;
  }

  return POPUP_ALLOWED_ROUTE_PATTERNS.some((pattern) =>
    matchPath({ path: pattern, end: true }, pathname),
  );
};

export const isPopupDismissedForWeek = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  if (!isBrowser()) return false;
  try {
    const raw = localStorage.getItem(getPopupDismissKey(popup));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { expiresAt?: string };
    if (!parsed?.expiresAt) return false;
    return new Date(parsed.expiresAt).getTime() > Date.now();
  } catch {
    return false;
  }
};

export const markPopupDismissedForWeek = (popup: Pick<PopupBanner, 'id' | 'updated_at' | 'dismiss_days'>) => {
  if (!isBrowser()) return;
  const expiresAt = new Date(Date.now() + popup.dismiss_days * 24 * 60 * 60 * 1000).toISOString();
  localStorage.setItem(
    getPopupDismissKey(popup),
    JSON.stringify({ expiresAt, dismissedAt: new Date().toISOString() }),
  );
};

export const isPopupClosedForSession = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  if (!isBrowser()) return false;
  return sessionStorage.getItem(getPopupSessionKey(popup)) === '1';
};

export const markPopupClosedForSession = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  if (!isBrowser()) return;
  sessionStorage.setItem(getPopupSessionKey(popup), '1');
};

export const clearPopupSessionState = (popup: Pick<PopupBanner, 'id' | 'updated_at'>) => {
  if (!isBrowser()) return;
  sessionStorage.removeItem(getPopupSessionKey(popup));
};

export const listPopupBanners = async (): Promise<PopupBanner[]> => {
  await getAdminSessionUser();
  const { data, error } = await supabase
    .from('popup_banners')
    .select('*')
    .order('updated_at', { ascending: false })
    .order('priority', { ascending: false });

  if (error) {
    throw new Error(`팝업 목록 조회 실패: ${getErrorMessage(error)}`);
  }

  return ((data || []) as PopupBanner[]).map(normalizePopupBanner);
};

export const createPopupBanner = async (input: PopupBannerUpsertInput): Promise<PopupBanner> => {
  const user = await getAdminSessionUser();
  const { data, error } = await supabase
    .from('popup_banners')
    .insert({
      ...input,
      placement: 'service-entry',
      status: input.status || 'draft',
      is_active: input.is_active ?? false,
      created_by: user.id,
      updated_by: user.id,
    })
    .select('*')
    .single();

  if (error) {
    throw new Error(`팝업 생성 실패: ${getErrorMessage(error)}`);
  }

  return normalizePopupBanner(data as PopupBanner);
};

export const updatePopupBanner = async (
  id: string,
  input: Partial<PopupBannerUpsertInput>,
): Promise<PopupBanner> => {
  const user = await getAdminSessionUser();
  const { data, error } = await supabase
    .from('popup_banners')
    .update({
      ...input,
      updated_by: user.id,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`팝업 수정 실패: ${getErrorMessage(error)}`);
  }

  return normalizePopupBanner(data as PopupBanner);
};

export const deletePopupAsset = async (storagePath?: string | null) => {
  if (!storagePath) return;
  const { error } = await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
  if (error) {
    throw new Error(`팝업 이미지 삭제 실패: ${getErrorMessage(error)}`);
  }
};

export const deletePopupBanner = async (popup: PopupBanner) => {
  await getAdminSessionUser();

  const paths = [popup.web_storage_path, popup.mobile_storage_path].filter(Boolean) as string[];
  if (paths.length > 0) {
    const { error: removeError } = await supabase.storage.from(BUCKET_NAME).remove(paths);
    if (removeError) {
      throw new Error(`팝업 이미지 삭제 실패: ${getErrorMessage(removeError)}`);
    }
  }

  const { error } = await supabase.from('popup_banners').delete().eq('id', popup.id);
  if (error) {
    throw new Error(`팝업 삭제 실패: ${getErrorMessage(error)}`);
  }
};

export const uploadPopupAsset = async (params: {
  popupId: string;
  target: PopupAssetTarget;
  file: File;
  previousStoragePath?: string | null;
}): Promise<PopupAssetUploadResult> => {
  await getAdminSessionUser();

  const validation = validateImageFile(params.file);
  if (!validation.valid) {
    throw new Error(validation.error || '이미지 파일 검증에 실패했습니다.');
  }

  if (!isAllowedPopupImageType(params.file)) {
    throw new Error('JPG, PNG, WEBP 형식만 지원합니다.');
  }

  const compressed = await compressImage(params.file, {
    maxFileSizeBytes: 2 * 1024 * 1024,
    maxDimension: params.target === 'web' ? 1800 : 1600,
  });

  const fileToUpload = compressed.file;
  const { width, height } = await loadImageDimensions(fileToUpload);
  const ext = makeSafeExt(fileToUpload.name, fileToUpload.type);
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const storagePath = `${params.popupId}/${params.target}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(storagePath, fileToUpload, {
      cacheControl: '3600',
      upsert: false,
      contentType: fileToUpload.type || undefined,
    });

  if (uploadError) {
    throw new Error(`팝업 이미지 업로드 실패: ${uploadError.message}`);
  }

  const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(storagePath);

  if (params.previousStoragePath) {
    const { error: removeError } = await supabase.storage.from(BUCKET_NAME).remove([params.previousStoragePath]);
    if (removeError) {
      console.warn('기존 팝업 이미지 삭제 실패:', removeError.message);
    }
  }

  return {
    imageUrl: urlData.publicUrl,
    storagePath,
    width,
    height,
    fileSize: fileToUpload.size,
  };
};

export const publishPopupBanner = async (
  popupId: string,
  action: 'publish' | 'deactivate',
): Promise<PopupBanner> => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.access_token) {
    throw new Error('관리자 로그인이 필요합니다.');
  }

  const response = await fetch(`${getPopupApiBaseUrl()}/admin-popup-publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
    body: JSON.stringify({ popupId, action }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || '팝업 게시 처리에 실패했습니다.');
  }

  return normalizePopupBanner(payload.popup as PopupBanner);
};

export const getActiveServiceEntryPopup = async (): Promise<PopupBanner | null> => {
  const { data, error } = await supabase
    .from('popup_banners')
    .select('*')
    .eq('placement', 'service-entry')
    .order('priority', { ascending: false })
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(`활성 팝업 조회 실패: ${getErrorMessage(error)}`);
  }

  return data ? normalizePopupBanner(data as PopupBanner) : null;
};
